/**
 * Pixel-perfect Crop modal.
 *
 * - Independent of the Editor — has its own UI for crop/rotate.
 * - Zoom (mouse wheel + buttons), pan (space-drag or middle-mouse).
 * - Drag corner/edge handles or type exact W/H/X/Y.
 * - Lock aspect ratio (Free / Original / 1:1 / 4:3 / 3:2 / 16:9 / custom).
 * - Rotate 90° CW/CCW + free-angle input.
 * - On confirm: downloads PNG of the cropped (and rotated) result.
 *   "Send to inputs" adds it as a new source in the queue.
 */

import type { QueueStore } from '@/lib/queue/store';

export interface CropModalHandle {
  open(): void;
  close(): void;
}

interface CropRect { x: number; y: number; w: number; h: number; }
type AspectMode = 'free' | 'original' | '1:1' | '4:3' | '3:2' | '16:9';

export function createCropModal(store: QueueStore): CropModalHandle {
  // ── Top-level DOM ───────────────────────────────────────────────────────
  const backdrop = document.createElement('div');
  backdrop.className = 'rd-modal-backdrop crop-dialog-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', 'Crop image');

  const dialog = document.createElement('div');
  dialog.className = 'rd-modal crop-dialog';

  const header = document.createElement('div');
  header.className = 'rd-modal__header';
  const title = document.createElement('h2');
  title.className = 'rd-modal__title';
  title.textContent = 'Crop';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'rd-modal__close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';
  header.append(title, closeBtn);

  const body = document.createElement('div');
  body.className = 'rd-modal__content crop-dialog__body';

  // Left: canvas viewport. Right: controls.
  const viewport = document.createElement('div');
  viewport.className = 'crop-viewport';

  const canvasWrap = document.createElement('div');
  canvasWrap.className = 'crop-canvas-wrap';

  const imgCanvas = document.createElement('canvas');
  imgCanvas.className = 'crop-canvas';
  imgCanvas.tabIndex = 0;

  const overlay = document.createElement('div');
  overlay.className = 'crop-overlay';

  const selBox = document.createElement('div');
  selBox.className = 'crop-selbox';

  // 8 handles for resize (corners + edges)
  const handlePositions: Array<['nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w', string]> = [
    ['nw','crop-h crop-h--nw'],['n','crop-h crop-h--n'],['ne','crop-h crop-h--ne'],
    ['e','crop-h crop-h--e'],['se','crop-h crop-h--se'],
    ['s','crop-h crop-h--s'],['sw','crop-h crop-h--sw'],['w','crop-h crop-h--w'],
  ];
  const handleEls: Record<string, HTMLDivElement> = {};
  for (const [k, cls] of handlePositions) {
    const h = document.createElement('div');
    h.className = cls;
    h.dataset.handle = k;
    selBox.appendChild(h);
    handleEls[k] = h;
  }

  overlay.appendChild(selBox);
  canvasWrap.appendChild(imgCanvas);
  canvasWrap.appendChild(overlay);
  viewport.appendChild(canvasWrap);

  // Right controls
  const controls = document.createElement('div');
  controls.className = 'crop-controls';
  controls.innerHTML = `
    <div class="crop-section">
      <label class="crop-label">Crop area (px)</label>
      <div class="crop-grid2">
        <label>X<input type="number" class="rd-input crop-num" data-k="x" min="0" step="1"></label>
        <label>Y<input type="number" class="rd-input crop-num" data-k="y" min="0" step="1"></label>
        <label>W<input type="number" class="rd-input crop-num" data-k="w" min="1" step="1"></label>
        <label>H<input type="number" class="rd-input crop-num" data-k="h" min="1" step="1"></label>
      </div>
    </div>
    <div class="crop-section">
      <label class="crop-label">Aspect ratio</label>
      <select class="rd-select crop-aspect">
        <option value="free">Free</option>
        <option value="original">Match original</option>
        <option value="1:1">Square (1:1)</option>
        <option value="4:3">4:3</option>
        <option value="3:2">3:2</option>
        <option value="16:9">16:9</option>
      </select>
    </div>
    <div class="crop-section">
      <label class="crop-label">Rotate</label>
      <div class="crop-rotate-row">
        <button type="button" class="rd-btn rd-btn--secondary crop-rot-ccw" title="Rotate 90° CCW">↺ 90°</button>
        <button type="button" class="rd-btn rd-btn--secondary crop-rot-cw"  title="Rotate 90° CW">↻ 90°</button>
        <label class="crop-free-rot">
          Angle
          <input type="number" class="rd-input crop-angle" min="-360" max="360" step="0.1" value="0">°
        </label>
      </div>
    </div>
    <div class="crop-section">
      <label class="crop-label">Zoom</label>
      <div class="crop-zoom-row">
        <button type="button" class="rd-btn rd-btn--secondary crop-zoom-out" aria-label="Zoom out">−</button>
        <span class="crop-zoom-readout">100%</span>
        <button type="button" class="rd-btn rd-btn--secondary crop-zoom-in" aria-label="Zoom in">+</button>
        <button type="button" class="rd-btn rd-btn--secondary crop-zoom-fit">Fit</button>
        <button type="button" class="rd-btn rd-btn--secondary crop-zoom-100">1:1</button>
      </div>
    </div>
    <div class="crop-section crop-section--actions">
      <button type="button" class="rd-btn rd-btn--primary crop-download">Crop &amp; download</button>
    </div>
    <p class="crop-hint">Drag inside the box to move · drag handles to resize · scroll to zoom · hold Space + drag to pan.</p>
  `;

  body.append(viewport, controls);
  dialog.append(header, body);
  backdrop.appendChild(dialog);

  // ── State ───────────────────────────────────────────────────────────────
  let bitmap: ImageBitmap | null = null;
  let imgW = 0, imgH = 0;
  let zoom = 1;
  let panX = 0, panY = 0;
  let crop: CropRect = { x: 0, y: 0, w: 1, h: 1 };
  let rotation = 0; // arbitrary degrees
  let aspectMode: AspectMode = 'free';
  let panning = false;
  let spaceDown = false;
  let dragging: { kind: 'move' | 'resize'; handle?: string; startX: number; startY: number; orig: CropRect } | null = null;
  let sourceName = 'image';

  // ── Helpers ─────────────────────────────────────────────────────────────
  function getControl<T extends HTMLElement>(sel: string): T {
    return controls.querySelector(sel) as T;
  }
  const xIn = getControl<HTMLInputElement>('input[data-k="x"]');
  const yIn = getControl<HTMLInputElement>('input[data-k="y"]');
  const wIn = getControl<HTMLInputElement>('input[data-k="w"]');
  const hIn = getControl<HTMLInputElement>('input[data-k="h"]');
  const aspSel = getControl<HTMLSelectElement>('.crop-aspect');
  const angleIn = getControl<HTMLInputElement>('.crop-angle');
  const zoomReadout = getControl<HTMLSpanElement>('.crop-zoom-readout');

  function aspectRatio(): number | null {
    if (aspectMode === 'free') return null;
    if (aspectMode === 'original') return imgW / imgH;
    const [a, b] = aspectMode.split(':').map(Number);
    return a / b;
  }

  function clampCropToImage(c: CropRect): CropRect {
    const w = Math.max(1, Math.round(c.w));
    const h = Math.max(1, Math.round(c.h));
    const x = Math.max(0, Math.min(imgW - w, Math.round(c.x)));
    const y = Math.max(0, Math.min(imgH - h, Math.round(c.y)));
    return { x, y, w: Math.min(w, imgW - x), h: Math.min(h, imgH - y) };
  }

  function setCrop(next: CropRect, opts?: { skipAspect?: boolean }): void {
    if (!opts?.skipAspect) {
      const ar = aspectRatio();
      if (ar !== null) {
        // Adjust H from W (or vice versa) to honour aspect.
        const newH = Math.round(next.w / ar);
        next = { ...next, h: newH };
      }
    }
    crop = clampCropToImage(next);
    syncInputs();
    drawOverlay();
  }

  function syncInputs(): void {
    xIn.value = String(crop.x);
    yIn.value = String(crop.y);
    wIn.value = String(crop.w);
    hIn.value = String(crop.h);
  }

  function fitZoom(): void {
    if (!imgW || !imgH) return;
    const r = viewport.getBoundingClientRect();
    const pad = 24;
    // Fit the rotated bounding box, not the original — otherwise a 90°
    // rotation of a wide image would overflow the viewport.
    const rad = rotation * Math.PI / 180;
    const sin = Math.abs(Math.sin(rad)), cos = Math.abs(Math.cos(rad));
    const bw = imgW * cos + imgH * sin;
    const bh = imgW * sin + imgH * cos;
    const zx = (r.width - pad * 2) / bw;
    const zy = (r.height - pad * 2) / bh;
    zoom = Math.max(0.05, Math.min(zx, zy));
    panX = (r.width - imgW * zoom) / 2;
    panY = (r.height - imgH * zoom) / 2;
    drawAll();
  }

  function drawAll(): void {
    if (!bitmap) return;
    const r = viewport.getBoundingClientRect();
    imgCanvas.width = Math.max(1, Math.floor(r.width));
    imgCanvas.height = Math.max(1, Math.floor(r.height));
    canvasWrap.style.width = r.width + 'px';
    canvasWrap.style.height = r.height + 'px';
    // Rotate the wrapper (bitmap canvas + crop overlay) together around its
    // centre so rotation is reflected in the live preview. The crop selection
    // and bitmap stay aligned because they share the same rotated container.
    canvasWrap.style.transformOrigin = '50% 50%';
    canvasWrap.style.transform = rotation === 0 ? '' : `rotate(${rotation}deg)`;
    const ctx = imgCanvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = 'rgba(0,0,0,0.04)';
    ctx.fillRect(0, 0, imgCanvas.width, imgCanvas.height);
    ctx.imageSmoothingEnabled = zoom < 1;
    ctx.drawImage(bitmap, panX, panY, imgW * zoom, imgH * zoom);
    zoomReadout.textContent = `${Math.round(zoom * 100)}%`;
    drawOverlay();
  }

  function drawOverlay(): void {
    const left = panX + crop.x * zoom;
    const top  = panY + crop.y * zoom;
    selBox.style.left = `${left}px`;
    selBox.style.top  = `${top}px`;
    selBox.style.width  = `${crop.w * zoom}px`;
    selBox.style.height = `${crop.h * zoom}px`;
  }

  // ── Pointer interactions ────────────────────────────────────────────────
  function pointerToImage(e: PointerEvent): { x: number; y: number } {
    // canvasWrap is sized to the viewport and rotated around its center.
    // Use the viewport's (unrotated) rect to find the rotation pivot, then
    // apply the inverse rotation so cursor coords map back to image space.
    const v = viewport.getBoundingClientRect();
    const cx = v.left + v.width / 2;
    const cy = v.top + v.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const rad = -rotation * Math.PI / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const ux = dx * cos - dy * sin + cx;
    const uy = dx * sin + dy * cos + cy;
    return {
      x: Math.round((ux - v.left - panX) / zoom),
      y: Math.round((uy - v.top - panY) / zoom),
    };
  }

  selBox.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    const handle = target.dataset.handle;
    e.stopPropagation();
    const p = pointerToImage(e);
    dragging = {
      kind: handle ? 'resize' : 'move',
      handle,
      startX: p.x, startY: p.y,
      orig: { ...crop },
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  });

  selBox.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const p = pointerToImage(e);
    const dx = p.x - dragging.startX;
    const dy = p.y - dragging.startY;
    if (dragging.kind === 'move') {
      setCrop({ ...dragging.orig, x: dragging.orig.x + dx, y: dragging.orig.y + dy }, { skipAspect: true });
    } else {
      let { x, y, w, h } = dragging.orig;
      const handle = dragging.handle ?? '';
      if (handle.includes('w')) { x += dx; w -= dx; }
      if (handle.includes('e')) { w += dx; }
      if (handle.includes('n')) { y += dy; h -= dy; }
      if (handle.includes('s')) { h += dy; }
      const ar = aspectRatio();
      if (ar !== null) {
        // Lock aspect by adjusting H from W on E/W handles, W from H on N/S.
        if (handle === 'n' || handle === 's') {
          const newW = Math.round(h * ar);
          x = dragging.orig.x + Math.round((dragging.orig.w - newW) / 2);
          w = newW;
        } else {
          h = Math.round(w / ar);
          if (handle.includes('n')) y = dragging.orig.y + dragging.orig.h - h;
        }
      }
      setCrop({ x, y, w: Math.max(1, w), h: Math.max(1, h) }, { skipAspect: true });
    }
  });

  selBox.addEventListener('pointerup', () => { dragging = null; });
  selBox.addEventListener('pointercancel', () => { dragging = null; });

  // Pan: space + drag, or middle button
  let panStart: { x: number; y: number; px: number; py: number } | null = null;
  canvasWrap.addEventListener('pointerdown', (e) => {
    if (e.button === 1 || (e.button === 0 && spaceDown)) {
      panning = true;
      panStart = { x: e.clientX, y: e.clientY, px: panX, py: panY };
      canvasWrap.setPointerCapture(e.pointerId);
      e.preventDefault();
    }
  });
  canvasWrap.addEventListener('pointermove', (e) => {
    if (!panning || !panStart) return;
    // Inverse-rotate the screen-space drag delta so pan tracks the cursor
    // visually even when the wrapper is rotated.
    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;
    const rad = -rotation * Math.PI / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    panX = panStart.px + (dx * cos - dy * sin);
    panY = panStart.py + (dx * sin + dy * cos);
    drawAll();
  });
  canvasWrap.addEventListener('pointerup', () => { panning = false; panStart = null; });

  // Zoom on wheel
  canvasWrap.addEventListener('wheel', (e) => {
    e.preventDefault();
    // Inverse-rotate the cursor into the unrotated canvasWrap frame so the
    // zoom anchors on the visual point under the pointer even when the
    // image is rotated.
    const v = viewport.getBoundingClientRect();
    const pivotX = v.left + v.width / 2;
    const pivotY = v.top + v.height / 2;
    const dx = e.clientX - pivotX;
    const dy = e.clientY - pivotY;
    const rad = -rotation * Math.PI / 180;
    const cosR = Math.cos(rad), sinR = Math.sin(rad);
    const ux = dx * cosR - dy * sinR + pivotX;
    const uy = dx * sinR + dy * cosR + pivotY;
    const cx = ux - v.left;
    const cy = uy - v.top;
    const imgX = (cx - panX) / zoom;
    const imgY = (cy - panY) / zoom;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    zoom = Math.max(0.05, Math.min(32, zoom * factor));
    panX = cx - imgX * zoom;
    panY = cy - imgY * zoom;
    drawAll();
  }, { passive: false });

  // Keyboard pan flag
  document.addEventListener('keydown', (e) => {
    if (e.key === ' ' && backdrop.parentElement) { spaceDown = true; canvasWrap.style.cursor = 'grab'; }
  });
  document.addEventListener('keyup', (e) => {
    if (e.key === ' ') { spaceDown = false; canvasWrap.style.cursor = ''; }
  });

  // ── Numeric inputs ──────────────────────────────────────────────────────
  function bindNumInput(input: HTMLInputElement, key: keyof CropRect): void {
    input.addEventListener('change', () => {
      const n = Math.round(Number(input.value));
      if (!Number.isFinite(n)) return;
      setCrop({ ...crop, [key]: n });
    });
  }
  bindNumInput(xIn, 'x');
  bindNumInput(yIn, 'y');
  bindNumInput(wIn, 'w');
  bindNumInput(hIn, 'h');

  aspSel.addEventListener('change', () => {
    aspectMode = aspSel.value as AspectMode;
    setCrop(crop);
  });

  // Rotation
  const rotCcw = controls.querySelector('.crop-rot-ccw') as HTMLButtonElement;
  const rotCw  = controls.querySelector('.crop-rot-cw')  as HTMLButtonElement;
  rotCcw.addEventListener('click', () => {
    rotation = ((rotation - 90) % 360 + 360) % 360;
    angleIn.value = String(rotation);
    // Refit on 90° steps so the rotated image fits inside the viewport.
    fitZoom();
  });
  rotCw.addEventListener('click',  () => {
    rotation = (rotation + 90) % 360;
    angleIn.value = String(rotation);
    fitZoom();
  });
  angleIn.addEventListener('change', () => {
    const n = Number(angleIn.value);
    if (!Number.isFinite(n)) return;
    rotation = n;
    // Free-angle: redraw immediately; let the user manage zoom themselves.
    drawAll();
  });

  // Zoom buttons
  (controls.querySelector('.crop-zoom-in')  as HTMLButtonElement).addEventListener('click', () => { zoom = Math.min(32, zoom * 1.25); drawAll(); });
  (controls.querySelector('.crop-zoom-out') as HTMLButtonElement).addEventListener('click', () => { zoom = Math.max(0.05, zoom / 1.25); drawAll(); });
  (controls.querySelector('.crop-zoom-fit') as HTMLButtonElement).addEventListener('click', () => fitZoom());
  (controls.querySelector('.crop-zoom-100') as HTMLButtonElement).addEventListener('click', () => { zoom = 1; drawAll(); });

  // ── Render output ───────────────────────────────────────────────────────
  async function renderOutput(): Promise<{ blob: Blob; name: string } | null> {
    if (!bitmap) return null;
    // Step 1: crop into an offscreen canvas at the crop's pixel size.
    const cropC = document.createElement('canvas');
    cropC.width = crop.w;
    cropC.height = crop.h;
    const cctx = cropC.getContext('2d');
    if (!cctx) return null;
    cctx.drawImage(bitmap, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);

    // Step 2: rotate if needed.
    let final: HTMLCanvasElement = cropC;
    if (rotation !== 0) {
      const rad = rotation * Math.PI / 180;
      const sin = Math.abs(Math.sin(rad));
      const cos = Math.abs(Math.cos(rad));
      const rw = Math.round(crop.w * cos + crop.h * sin);
      const rh = Math.round(crop.w * sin + crop.h * cos);
      const rotC = document.createElement('canvas');
      rotC.width = rw; rotC.height = rh;
      const rctx = rotC.getContext('2d');
      if (!rctx) return null;
      rctx.translate(rw / 2, rh / 2);
      rctx.rotate(rad);
      rctx.drawImage(cropC, -crop.w / 2, -crop.h / 2);
      final = rotC;
    }

    const blob: Blob = await new Promise((resolve, reject) => {
      final.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
    });
    const dot = sourceName.lastIndexOf('.');
    const base = dot >= 0 ? sourceName.slice(0, dot) : sourceName;
    return { blob, name: `${base}-cropped.png` };
  }

  (controls.querySelector('.crop-download') as HTMLButtonElement).addEventListener('click', async () => {
    const out = await renderOutput();
    if (!out) return;
    const url = URL.createObjectURL(out.blob);
    const a = document.createElement('a');
    a.href = url; a.download = out.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });

  // ── Open / close ────────────────────────────────────────────────────────
  function pickSourceFile(): File | null {
    const { selectedSourceId, items } = store.getState();
    const src = items.find(i => i.id === selectedSourceId && i.isSource)
      ?? items.find(i => i.isSource);
    return src?.file ?? null;
  }

  async function loadFile(file: File): Promise<void> {
    sourceName = file.name;
    if (bitmap) { bitmap.close(); bitmap = null; }
    bitmap = await createImageBitmap(file);
    imgW = bitmap.width;
    imgH = bitmap.height;
    crop = { x: 0, y: 0, w: imgW, h: imgH };
    rotation = 0;
    angleIn.value = '0';
    aspectMode = 'free';
    aspSel.value = 'free';
    syncInputs();
    fitZoom();
  }

  function close(): void {
    if (!backdrop.parentElement) return;
    backdrop.parentElement.removeChild(backdrop);
    if (bitmap) { bitmap.close(); bitmap = null; }
  }

  function open(): void {
    const file = pickSourceFile();
    if (!file) return;
    if (backdrop.parentElement) return;
    document.body.appendChild(backdrop);
    void loadFile(file);
    // Refit on first frame after layout settles.
    requestAnimationFrame(() => fitZoom());
  }

  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && backdrop.parentElement) close();
  });

  // Resize listener
  window.addEventListener('resize', () => {
    if (backdrop.parentElement) drawAll();
  });

  return { open, close };
}
